import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import Layout from '../../components/Layout'
import './style.css'
import MatchOdds from '../../components/match-details/match-odds'
import Normal from '../../components/match-details/normal'
import Fancy from '../../components/match-details/fancy'
import Khado from '@/components/match-details/khado'
import OddEven from '../../components/match-details/oddeven'
import Meter from '@/components/match-details/meter'
import Market2 from '@/components/match-details/market2'
import Market9 from '@/components/match-details/market9'
import FancyMarket from '@/components/match-details/normal/FancyMarket'
import SportsViewMoreBetsModal from '../../components/SportsViewMoreBetsModal'
import BetLockModal from '../../components/BetLockModal'
import MarketList from '../../components/MarketList/MarketList'
import { getMatchPrivateData, getSportsData, getSportsScoreCard, getMatchExposureSummary, getMatchDownlineBets } from '../../services/api'
import { formatOdds } from '../../utils/formatOdds'
import { formatRefMatchTime } from '../../utils/refMatchTime'
import { IS_REF_MOBILE } from '../../utils/refViewport'


// Sport ID constants (matching database sport_id values)
const SPORT_IDS = {
  SOCCER: '1',
  TENNIS: '2',
  CRICKET: '4'
}

/**
 * GameDetails Page Component
 * 
 * Displays game details and betting markets based on sport type.
 * Conditionally renders betting components based on sportId:
 * - Cricket (sportId: 4): Shows all betting markets (MatchOdds, Normal, Fancy, Khado, OddEven, Meter, Market2, Market9)
 * - Soccer (sportId: 1): Shows only MatchOdds
 * 
 * Route: /game-details/:sportId/:eventId/:marketId
 * 
 * @example
 * /game-details/4/481589204/5746424906884 (Cricket - all markets)
 * /game-details/1/481589204/5746424906884 (Soccer - MatchOdds only)
 */
function GameDetails() {
  // Extract route parameters
  const { sportId, eventId, marketId } = useParams()

  // Parse and store active IDs
  const activeSportId = sportId ? String(sportId) : null
  const activeEventId = eventId ? String(eventId) : null
  const activeMarketId = marketId ? String(marketId) : null

  // Determine if current sport is Cricket (shows all markets)
  const isCricket = useMemo(() => activeSportId === SPORT_IDS.CRICKET, [activeSportId])

  // Determine if current sport is Soccer (shows only MatchOdds)
  const isSoccer = useMemo(() => activeSportId === SPORT_IDS.SOCCER, [activeSportId])

  // State management
  const [activeTab, setActiveTab] = useState('matched')
  const [showBetLockModal, setShowBetLockModal] = useState(false)
  const [betLockMarketName, setBetLockMarketName] = useState(null)
  // Initialize selectedMarket based on route - will be updated when markets are loaded
  const [selectedMarket, setSelectedMarket] = useState(null)
  const [markets, setMarkets] = useState([]) // Market names for MarketList (Soccer only)
  const [marketsData, setMarketsData] = useState([]) // Store full market objects (for both Soccer and Cricket)
  const lastRouteMarketIdRef = useRef(null) // Track last route marketId to detect actual route changes
  const isManualSelectionRef = useRef(false) // Track if selection was manual
  const [matchInfo, setMatchInfo] = useState({ name: '', series: '', time: '' }) // Match details for header
  const [scoreCardUrl, setScoreCardUrl] = useState(null)

  // Streaming State (mirrors FallbackStreamIframe logic from frontend)
  const [useFallbackStream, setUseFallbackStream] = useState(false)
  const streamLoadedRef = useRef(false)

  // Store exposure data scoped per market so two markets that share runner names
  // (e.g. MATCH_ODDS vs Bookmaker) don't bleed into each other.
  // byMarketId: { [marketId]: { [selectionName]: amount } }
  // byGameType: { [gameTypeLower]: { [selectionName]: amount } } (fallback match)
  // list: [{ marketId, gameType, marketName, name, exposure }] (for Book Summary)
  const [matchExposure, setMatchExposure] = useState({ byMarketId: {}, byGameType: {}, list: [] })
  const [matchedBets, setMatchedBets] = useState([])
  const [unmatchedBets, setUnmatchedBets] = useState([])
  const [, setBetsLoading] = useState(false)
  const [showViewMoreModal, setShowViewMoreModal] = useState(false)
  const [isTvExpanded, setIsTvExpanded] = useState(false)
  const [isBookSummaryOpen, setIsBookSummaryOpen] = useState(false)

  // Markets that have data - these will update MatchOdds component


  // Helper function to check if a market exists in marketsData
  // Supports case-insensitive matching for better compatibility
  const hasMarket = (marketName) => {
    if (!marketName) return false
    const lowerMarketName = marketName.toLowerCase()
    return marketsData.some(market => {
      const mname = market.mname || market.marketName || market.name
      if (!mname) return false
      // Case-insensitive comparison
      return mname.toLowerCase() === lowerMarketName
    })
  }

  /**
   * Fetch match markets from API for both Soccer and Cricket
   * Market names are extracted from the 'mname' field in the API response
   * @param {boolean} shouldSetMarket - Whether to set the selected market based on route
   */
  const fetchMatchMarkets = useCallback(async (shouldSetMarket = false) => {
    if (!activeEventId || !activeSportId) {
      return
    }

    try {
      const response = await getMatchPrivateData(activeEventId, activeSportId)
      console.log('Match private data response:', response)

      let marketArray = []

      // Handle different response structures - array, single object, or nested
      if (response && response.success) {
        // Case 1: data is an array (most common)
        if (Array.isArray(response.data)) {
          marketArray = response.data
        }
        // Case 2: nested data structure (response.data.data)
        else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          marketArray = response.data.data
        }
        // Case 3: data is a single object (convert to array) - check for mid, id, or mname
        else if (response.data && typeof response.data === 'object' && (response.data.mid || response.data.id || response.data.mname)) {
          marketArray = [response.data]
        }
        // Case 4: data is an object with array-like structure
        else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Try to extract array from object values
          const values = Object.values(response.data)
          if (values.length > 0) {
            // Check if first value looks like a market object
            if (values[0] && (values[0].mid || values[0].id || values[0].mname)) {
              marketArray = values
            }
            // Check if values contain arrays of markets
            else if (Array.isArray(values[0])) {
              marketArray = values[0]
            }
          }
        }
      }

      console.log('Extracted marketArray:', marketArray)
      console.log('Market names found:', marketArray.map(m => m?.mname || m?.marketName || m?.name || 'unknown'))

      // Store full market objects for matching by ID (for both Soccer and Cricket)
      setMarketsData(marketArray)

      // Extract market names from the markets array (for MarketList - Soccer only)
      // Always use 'mname' field as primary source for market name
      if (isSoccer) {
        const marketNames = marketArray
          .map(market => {
            // Primary source: mname field from API response
            const marketName = market.mname || market.marketName || market.name
            return marketName
          })
          .filter(name => name) // Remove any null/undefined values

        setMarkets(marketNames)

        // Only set market on initial load or when explicitly requested (Soccer)
        if (shouldSetMarket) {
          if (activeMarketId) {
            const activeMarket = marketArray.find(market => {
              const marketId = market.mid || market.marketId || market.id
              return String(marketId) === String(activeMarketId)
            })

            if (activeMarket) {
              // Use mname field from API response as market name
              const marketName = activeMarket.mname || activeMarket.marketName || activeMarket.name
              if (marketName) {
                setSelectedMarket(marketName)
                lastRouteMarketIdRef.current = activeMarketId
                isManualSelectionRef.current = false
                return
              }
            }
          }

          // Set default market if no market is selected and no marketId in route
          if (marketNames.length > 0) {
            const defaultMarket = marketNames.find(name => name === 'MATCH_ODDS') || marketNames[0] || 'MATCH_ODDS'
            setSelectedMarket(defaultMarket)
            isManualSelectionRef.current = false
          }
        }
      } else if (isCricket && shouldSetMarket) {
        // For Cricket, set market based on route marketId
        // Market name is extracted from mname field in API response
        if (activeMarketId) {
          const activeMarket = marketArray.find(market => {
            const marketId = market.mid || market.marketId || market.id
            return String(marketId) === String(activeMarketId)
          })

          if (activeMarket) {
            // Use mname field from API response as market name (e.g., "MATCH_ODDS", "fancy1", "oddeven", "TIED_MATCH")
            const marketName = activeMarket.mname || activeMarket.marketName || activeMarket.name
            if (marketName) {
              setSelectedMarket(marketName)
              lastRouteMarketIdRef.current = activeMarketId
              isManualSelectionRef.current = false
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching match markets:', error)
      // Don't clear markets on error, keep previous data
    }
  }, [activeEventId, activeSportId, activeMarketId, isSoccer, isCricket])

  /**
   * Fetch matched and unmatched bets for downline users only.
   * Uses the dedicated /admin/reports/match-downline-bets endpoint which
   * enforces hierarchy filtering (owner → full tree, staff → descendants).
   */
  const fetchBets = useCallback(async () => {
    if (!activeEventId) return

    setBetsLoading(true)
    try {
      const params = {
        eventId: activeEventId,
        limit: 100
      }

      const response = await getMatchDownlineBets(params)
      if (response && response.success) {
        const allBets = response.data || []

        // Filter matched and unmatched bets based on the 'unmatched' flag
        // and fall back to status strings if flag is not present.
        const mBets = allBets.filter(bet => {
          if (bet.unmatched === true) return false;
          if (bet.unmatched === false) return true;
          // Fallback to status if unmatched flag is missing
          return ['matched', 'Match', 'MATCHED'].includes(bet.status);
        });

        const uBets = allBets.filter(bet => {
          if (bet.unmatched === true) return true;
          if (bet.unmatched === false) return false;
          // Fallback to status if unmatched flag is missing
          return ['OPEN', 'PENDING', 'open', 'pending'].includes(bet.status);
        });

        setMatchedBets(mBets)
        setUnmatchedBets(uBets)
      }
    } catch (error) {
      console.error('Error fetching downline bets:', error)
    } finally {
      setBetsLoading(false)
    }
  }, [activeEventId])

  // Fetch match details for header (works for both Soccer and Cricket)
  const fetchMatchDetails = useCallback(async () => {
    if (!activeEventId || !activeSportId) {
      return
    }

    try {
      const response = await getSportsData(activeSportId)
      const actualData = response.data?.data || response.data || response
      const allEvents = [...(actualData.t1 || []), ...(actualData.t2 || [])]

      // Find the match by gmid (eventId)
      const match = allEvents.find(event => String(event.gmid) === String(activeEventId))

      if (match) {
        const seriesName = match.cname || ''
        const matchName = match.ename || match.name || ''
        const startTime = match.stime || ''

        setMatchInfo({
          name: matchName,
          series: seriesName,
          time: startTime,
          // Reference shows the Live TV bar only when the event has tv, and the score card only
          // while the match is in play — both flags come with this same event record.
          tv: !!match.tv,
          iplay: !!match.iplay
        })
      } else {
        // If match not found, set default/fallback
        setMatchInfo({
          name: 'Match Details',
          series: '',
          time: ''
        })
      }
    } catch (error) {
      console.error('Error fetching match details:', error)
      // Set fallback on error
      setMatchInfo({
        name: 'Match Details',
        series: '',
        time: ''
      })
    }
  }, [activeEventId, activeSportId])

  // Fetch match details when eventId or sportId changes
  useEffect(() => {
    if (activeEventId && activeSportId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchMatchDetails()

      const fetchScoreCard = async () => {
        try {
          const url = await getSportsScoreCard(activeEventId, activeSportId)
          setScoreCardUrl(url)
        } catch (error) {
          console.error("Error fetching scorecard:", error)
        }
      }
      fetchScoreCard()
    }
  }, [activeEventId, activeSportId, fetchMatchDetails])

  // Initial fetch and debounced updates (every 2 seconds) - For both Soccer and Cricket
  useEffect(() => {
    if (!activeEventId || !activeSportId) {
      return
    }

    const fetchExposure = async () => {
      const token = localStorage.getItem('ownerToken') || localStorage.getItem('staffToken') || localStorage.getItem('token')
      if (activeEventId) {
        try {
          const res = await getMatchExposureSummary(token, activeEventId);
          console.log('Match exposure initial response:', res)
          if (res && res.success && res.data) {
            // Keep exposure scoped per market. The backend already returns
            // markets[] each with its own matchId (the market ID / mid the bet
            // was placed on) + gameType + selections{}. We must NOT flatten by
            // selection name only, otherwise markets that share runner names
            // (MATCH_ODDS vs Bookmaker -> England/New Zealand/The Draw) collide
            // and the exposure renders against the wrong market.
            const byMarketId = {}
            const byGameType = {}
            const list = []
            const rawData = res.data

            const extractFromMarket = (market) => {
              if (!market || !market.selections || typeof market.selections !== 'object') return
              const midKey = String(market.matchId ?? market.id ?? '')
              const typeKey = String(market.gameType ?? '').trim().toLowerCase()
              if (midKey && !byMarketId[midKey]) byMarketId[midKey] = {}
              if (typeKey && !byGameType[typeKey]) byGameType[typeKey] = {}
              Object.entries(market.selections).forEach(([name, val]) => {
                const amt = Number(val) || 0
                if (midKey) byMarketId[midKey][name] = amt
                if (typeKey) byGameType[typeKey][name] = amt
                list.push({ marketId: midKey, gameType: market.gameType, marketName: market.marketName, name, exposure: amt })
              })
            }

            const extractFromMarkets = (markets) => {
              if (Array.isArray(markets)) {
                markets.forEach(extractFromMarket)
              } else if (markets && typeof markets === 'object') {
                Object.values(markets).forEach(extractFromMarket)
              }
            }

            if (Array.isArray(rawData)) {
              rawData.forEach(event => { if (event.markets) extractFromMarkets(event.markets) })
            } else if (rawData && typeof rawData === 'object') {
              if (rawData.markets) extractFromMarkets(rawData.markets)
              else if (rawData.selections) extractFromMarket(rawData)
            }
            setMatchExposure({ byMarketId, byGameType, list })
          }
        } catch (err) {
          console.error('Error fetching match exposure:', err)
        }
      }
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchMatchMarkets(true)
    fetchBets()
    fetchExposure()

    // Set up interval to fetch data every 2 seconds (only update data, don't change selection)
    const intervalId = setInterval(() => {
      fetchMatchMarkets(false) // Don't set market during periodic updates
    }, 2000) // 2 seconds

    // Interval for exposure and bets (5 seconds is enough)
    const slowIntervalId = setInterval(() => {
      fetchExposure()
      fetchBets()
    }, 5000)

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId)
      clearInterval(slowIntervalId)
    }
  }, [activeEventId, activeSportId, fetchMatchMarkets, fetchBets, isTvExpanded])

  // Reset + arm fallback timer whenever the match (eventId) changes or TV is opened
  useEffect(() => {
    setUseFallbackStream(false)
    streamLoadedRef.current = false
    if (activeEventId && isTvExpanded) {
      const t = setTimeout(() => {
        if (!streamLoadedRef.current) setUseFallbackStream(true)
      }, 6000) // 6 second timeout for primary stream
      return () => clearTimeout(t)
    }
  }, [activeEventId, isTvExpanded])

  // Bets and exposure are handled in the main polling loop above

  // Update selected market only when route marketId actually changes (not on data updates)
  // Works for both Soccer and Cricket - uses mname field from API response
  useEffect(() => {
    // Only update if route marketId actually changed (different from last one we processed)
    if (activeMarketId && activeMarketId !== lastRouteMarketIdRef.current) {
      // Don't override if it's a manual selection (unless route actually changed to a different marketId)
      // Since activeMarketId !== lastRouteMarketIdRef.current, route did change, so we should update

      if (marketsData.length > 0) {
        const activeMarket = marketsData.find(market => {
          const marketId = market.mid || market.marketId || market.id
          return String(marketId) === String(activeMarketId)
        })

        if (activeMarket) {
          // Use mname field from API response as market name
          const marketName = activeMarket.mname || activeMarket.marketName || activeMarket.name
          if (marketName) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedMarket(marketName) // Update when route marketId changes
            lastRouteMarketIdRef.current = activeMarketId
            isManualSelectionRef.current = false // Mark as route-based selection
          }
        }
      }
      // Update the ref to track this route marketId (even if data not loaded yet)
      lastRouteMarketIdRef.current = activeMarketId
    }
  }, [activeMarketId, marketsData]) // Include marketsData to update when data loads

  /**
   * Transform API market data to component format
   * Works for both Soccer and Cricket markets
   * @param {Object} apiMarket - Market data from API
   * @returns {Object} Transformed market data
   */
  const transformMarketData = (apiMarket) => {
    if (!apiMarket || !apiMarket.section || !Array.isArray(apiMarket.section)) {
      return null
    }

    // Always use mname from API response (primary source for market name)
    const marketName = apiMarket.mname || apiMarket.marketName || apiMarket.name

    // Helper function to convert odds - handles both formats (decimal or multiplied by 100)
    const convertOdds = (oddsValue) => {
      if (!oddsValue || oddsValue <= 0) return 0
      // If odds are > 100, they're likely multiplied by 100, so divide
      // Otherwise, they're already in decimal format
      if (oddsValue > 100) {
        return parseFloat((oddsValue / 100).toFixed(2))
      }
      return parseFloat(oddsValue.toFixed(2))
    }

    // Handle HT/FT market (Soccer - has 9 sections)
    if (marketName === 'HT/FT' || marketName === 'HT-FT') {
      const items = apiMarket.section.map(section => {
        // Find back odds (usually tno: 0, but check all tiers)
        const backOdd = section.odds?.find(odd => odd.otype === 'back' && odd.tno === 0) ||
          section.odds?.find(odd => odd.otype === 'back' && odd.tno === 1) ||
          section.odds?.find(odd => odd.otype === 'back')

        const oddsValue = backOdd ? convertOdds(backOdd.odds) : 0

        return {
          label: (section.nat || section.name || '').trim(),
          odds: oddsValue
        }
      })
      return {
        type: 'htft',
        items: items
      }
    }

    // Helper function to transform a single section
    const transformSection = (section) => {
      const backOdds = []
      const layOdds = []
      const backAmounts = []
      const layAmounts = []

      // Sort odds by tier (tno: 0, 1, 2)
      const sortedOdds = [...(section.odds || [])].sort((a, b) => a.tno - b.tno)

      sortedOdds.forEach(odd => {
        const oddsValue = convertOdds(odd.odds)
        const amount = odd.size || 0

        if (odd.otype === 'back') {
          backOdds.push(oddsValue)
          backAmounts.push(amount)
        } else if (odd.otype === 'lay') {
          layOdds.push(oddsValue)
          layAmounts.push(amount)
        }
      })

      // Ensure we have 3 tiers (pad with 0 if needed)
      while (backOdds.length < 3) {
        backOdds.push(0)
        backAmounts.push(0)
      }
      while (layOdds.length < 3) {
        layOdds.push(0)
        layAmounts.push(0)
      }

      return {
        name: section.nat || section.name || '',
        odds: [
          { back: backOdds[0] || 0, lay: layOdds[0] || 0 },
          { back: backOdds[1] || 0, lay: layOdds[1] || 0 },
          { back: backOdds[2] || 0, lay: layOdds[2] || 0 }
        ],
        amounts: [
          { back: backAmounts[0] || 0, lay: layAmounts[0] || 0 },
          { back: backAmounts[1] || 0, lay: layAmounts[1] || 0 },
          { back: backAmounts[2] || 0, lay: layAmounts[2] || 0 }
        ]
      }
    }

    // Handle markets with 2 sections (OVER_UNDER, fancy1, TIED_MATCH for Cricket, etc.)
    // Works for both Soccer and Cricket
    if (apiMarket.section.length === 2) {
      return {
        team1: transformSection(apiMarket.section[0]),
        team2: transformSection(apiMarket.section[1])
      }
    }

    // Handle markets with more than 3 sections (multi-column layout like HT/FT)
    // For markets like "Match Result/Both Teams to score" with 6+ sections
    if (apiMarket.section.length > 3) {
      const marketStatus = apiMarket.status || ''
      const items = apiMarket.section.map(section => {
        // Find back odds (usually tno: 0)
        const backOdd = section.odds?.find(odd => odd.otype === 'back' && odd.tno === 0) ||
          section.odds?.find(odd => odd.otype === 'back')

        const sectionGstatus = section.gstatus || ''
        const isSuspended = sectionGstatus.toUpperCase() === 'SUSPENDED' || marketStatus.toUpperCase() === 'SUSPENDED'

        const oddsValue = (backOdd && !isSuspended && backOdd.odds > 0) ? convertOdds(backOdd.odds) : 0

        return {
          label: (section.nat || section.name || '').trim(),
          odds: isSuspended ? null : oddsValue, // null means show "—" for suspended
          isSuspended: isSuspended,
          gstatus: sectionGstatus
        }
      })
      return {
        type: 'multi-column',
        items: items
      }
    }

    // Handle markets with exactly 3 sections (MATCH_ODDS for Cricket with 3 teams, HALF_TIME, etc.)
    // Works for both Soccer and Cricket
    if (apiMarket.section.length === 3) {
      return {
        team1: transformSection(apiMarket.section[0]),
        team2: transformSection(apiMarket.section[1]),
        team3: apiMarket.section[2] ? transformSection(apiMarket.section[2]) : null
      }
    }

    return null
  }

  /**
   * Get market data based on selected market
   * Uses API data for both Soccer and Cricket (market name from mname field)
   * Falls back to static data only if API data is not available
   * @param {string} marketName - Market name (from mname field in API response)
   * @returns {Object} Market data with team1 and team2
   */
  const getMarketData = (marketName) => {
    // Use API data for both Soccer and Cricket
    // Market name is always extracted from mname field in API response
    if (marketsData.length > 0) {
      const wantedName = String(marketName || '').toLowerCase()
      const apiMarket = marketsData.find(market => {
        // Always use mname as primary source for market name matching.
        // Case-insensitive to stay consistent with hasMarket() — the feed uses
        // 'Bookmaker' (capital B) while the render passes lowercase 'bookmaker',
        // which otherwise fell through to the Victoria/Western Australia default.
        const name = market.mname || market.marketName || market.name
        return String(name || '').toLowerCase() === wantedName
      })

      if (apiMarket) {
        const transformed = transformMarketData(apiMarket)
        if (transformed) {
          return transformed
        }
      }
    }

    // Fallback to static data for cricket or if API data not available
    const defaultData = {
      team1: { name: 'Victoria', odds: [{ back: 1.74, lay: 2.06 }, { back: 1.9, lay: 2.12 }, { back: 1.94, lay: 2.34 }], amounts: [{ back: 1, lay: 2 }, { back: 83.02, lay: 11.31 }, { back: 2.07, lay: 185.45 }] },
      team2: { name: 'Western Australia', odds: [{ back: 1.74, lay: 2.06 }, { back: 1.9, lay: 2.12 }, { back: 1.94, lay: 2.86 }], amounts: [{ back: 250.46, lay: 1.95 }, { back: 12.62, lay: 74.4 }, { back: 2.12, lay: 152.03 }] }
    }

    const marketData = {
      'MATCH_ODDS': defaultData,
      'OVER_UNDER_25': {
        team1: { name: 'Over 2.5', odds: [{ back: 1.85, lay: 2.10 }, { back: 1.95, lay: 2.20 }, { back: 2.05, lay: 2.30 }], amounts: [{ back: 150, lay: 200 }, { back: 95.5, lay: 125.8 }, { back: 45.2, lay: 78.9 }] },
        team2: { name: 'Under 2.5', odds: [{ back: 1.75, lay: 2.00 }, { back: 1.88, lay: 2.15 }, { back: 1.98, lay: 2.25 }], amounts: [{ back: 180.3, lay: 175.5 }, { back: 110.2, lay: 95.8 }, { back: 52.1, lay: 88.4 }] }
      },
      'OVER_UNDER_05': {
        team1: { name: 'Over 0.5', odds: [{ back: 1.20, lay: 1.35 }, { back: 1.25, lay: 1.40 }, { back: 1.30, lay: 1.45 }], amounts: [{ back: 500, lay: 450 }, { back: 320.5, lay: 280.8 }, { back: 180.2, lay: 195.9 }] },
        team2: { name: 'Under 0.5', odds: [{ back: 3.50, lay: 4.20 }, { back: 3.75, lay: 4.50 }, { back: 4.00, lay: 4.80 }], amounts: [{ back: 85.3, lay: 75.5 }, { back: 45.2, lay: 38.8 }, { back: 25.1, lay: 28.4 }] }
      },
      'OVER_UNDER_15': {
        team1: { name: 'Over 1.5', odds: [{ back: 1.45, lay: 1.65 }, { back: 1.55, lay: 1.75 }, { back: 1.65, lay: 1.85 }], amounts: [{ back: 280, lay: 320 }, { back: 195.5, lay: 225.8 }, { back: 95.2, lay: 128.9 }] },
        team2: { name: 'Under 1.5', odds: [{ back: 2.40, lay: 2.80 }, { back: 2.60, lay: 3.00 }, { back: 2.80, lay: 3.20 }], amounts: [{ back: 120.3, lay: 105.5 }, { back: 75.2, lay: 65.8 }, { back: 42.1, lay: 48.4 }] }
      },
      // HT/FT removed - will use API data only
    }

    return marketData[marketName] || defaultData
  }

  /**
   * Get the exposure slice for a single market, scoped by the market's own ID.
   * This mirrors the user panel logic (match exposure by market mid, not by
   * selection name) so that markets sharing runner names don't show each
   * other's exposure. Falls back to gameType when the market ID isn't matched.
   * @param {string} marketName - mname of the market (e.g. "MATCH_ODDS", "Bookmaker", "Normal")
   * @returns {Object} { [selectionName]: amount }
   */
  const getMarketExposure = (marketName) => {
    const byMarketId = matchExposure?.byMarketId || {}
    const byGameType = matchExposure?.byGameType || {}
    const wanted = String(marketName || '').trim().toLowerCase()

    // Resolve this market's ID (mid) from the live markets feed
    const apiMarket = marketsData.find(market => {
      const name = market.mname || market.marketName || market.name
      return String(name || '').trim().toLowerCase() === wanted
    })
    const mid = apiMarket && (apiMarket.mid || apiMarket.marketId || apiMarket.id)

    if (mid != null && byMarketId[String(mid)]) return byMarketId[String(mid)]
    if (byGameType[wanted]) return byGameType[wanted]
    return {}
  }

  /**
   * Handles market selection from MarketList component
   * @param {string} marketName - Name of the selected market
   */
  const handleMarketSelect = (marketName) => {
    // Set the selected market and mark it as manual selection
    setSelectedMarket(marketName)
    isManualSelectionRef.current = true // Mark as manual selection so it persists
  }

  /**
   * Format API created_at to display date (DD/MM/YYYY HH:mm:ss)
   */
  const formatBetDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      const h = String(d.getHours()).padStart(2, '0')
      const m = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${day}/${month}/${year} ${h}:${m}:${s}`
    } catch {
      return dateStr
    }
  }

  /**
   * Group bets by market_type and bet_type for table display (header + rows with back-border/lay-border)
   * Returns array of { marketType, betType, borderClass, date, bets } sorted by newest first
   */
  const groupBetsForTable = (bets) => {
    if (!bets || !bets.length) return []
    const map = new Map()
    bets.forEach((bet) => {
      const marketType = bet.market_type || bet.marketType || 'Market'
      const betType = (bet.bet_type || bet.betType || 'back').toLowerCase()

      // Determine if it's a lay-style bet (Lay or No)
      const selection = (bet.selection || bet.selection_name || '').toLowerCase()
      const isLay = betType === 'lay' || betType === 'no' || selection === 'no'

      const key = `${marketType}|${betType}`
      if (!map.has(key)) {
        map.set(key, {
          marketType,
          betType,
          borderClass: isLay ? 'lay-border' : 'back-border',
          bets: []
        })
      }
      map.get(key).bets.push(bet)
    })
    const groups = Array.from(map.values())
    groups.forEach((g) => {
      g.bets.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      g.date = g.bets[0]?.created_at
    })
    groups.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    return groups
  }

  /**
   * Handles Bet Lock button click
   * Opens the Bet Lock modal for account selection
   */
  const [betLockMarketId, setBetLockMarketId] = useState(null)

  const handleBetLockClick = (marketName) => {
    // Find the mid for this market from marketsData
    const market = marketsData.find(m => {
      const mname = m.mname || m.marketName || m.name
      return mname && mname.toLowerCase() === (marketName || '').toLowerCase()
    })
    const mid = market?.mid || activeMarketId
    setBetLockMarketId(mid ? String(mid) : null)
    setBetLockMarketName(marketName || 'MATCH_ODDS')
    setShowBetLockModal(true)
  }

  // Debug logging (remove in production)
  console.log('GameDetails Params:', {
    sportId: activeSportId,
    eventId: activeEventId,
    marketId: activeMarketId,
    isCricket,
    isSoccer
  })
  // Markets in the reference order: the feed sorted by `sno` (stable, ties keep feed order).
  const orderedMarkets = [...marketsData].sort((a, b) => (Number(a.sno) || 0) - (Number(b.sno) || 0))

  const marketProps = (m, i) => ({
    onBetLockClick: handleBetLockClick,
    sportId: activeSportId,
    eventId: activeEventId,
    marketId: activeMarketId,
    marketsData,
    market: m,
    index: i,
  })

  // One market of the feed -> its component (each renders its own reference `.market-N` root).
  // The reference picks the template by the feed's `dtype` (2 -> Market2, 4 -> Market4, 6 -> Market6,
  // 7 fancy1, 8 oddeven, 9 -> Market9, 10 khado). Known fancy-type names keep their own wrappers;
  // everything else is dispatched by dtype, falling back to gtype when dtype is missing.
  const renderMarket = (m, i) => {
    const mname = m.mname || m.marketName || m.name || ''
    const key = `${m.mid || mname}-${i}`
    const gtype = String(m.gtype || '').toLowerCase()
    const dtype = Number(m.dtype)
    const exposure = getMarketExposure(mname)
    switch (mname.toLowerCase()) {
      case 'normal':
        return <Normal key={key} {...marketProps(m, i)} exposureData={getMarketExposure('Normal')} />
      case 'fancy1':
        return <Fancy key={key} {...marketProps(m, i)} exposureData={getMarketExposure('fancy1')} />
      case 'khado':
        return <Khado key={key} {...marketProps(m, i)} exposureData={getMarketExposure('khado')} />
      case 'oddeven':
        return <OddEven key={key} {...marketProps(m, i)} exposureData={getMarketExposure('oddeven')} />
      case 'meter':
        return <Meter key={key} {...marketProps(m, i)} exposureData={getMarketExposure('meter')} />
      default:
        break
    }
    const fancyMarket = (layout) => (
      <FancyMarket
        key={key}
        marketName={mname}
        betLockName={mname}
        layout={layout}
        index={i}
        onBetLockClick={handleBetLockClick}
        marketsData={marketsData}
        exposureData={exposure}
      />
    )
    if (dtype === 9 || gtype === 'cricketcasino') {
      return <Market9 key={key} market={m} index={i} onBetLockClick={handleBetLockClick} exposureData={exposure} />
    }
    if (dtype === 6 || (!dtype && gtype === 'fancy')) return fancyMarket('noyes')
    if (dtype === 7 || (!dtype && gtype === 'fancy1')) return fancyMarket('backlay')
    if (dtype === 8 || (!dtype && gtype === 'oddeven')) return fancyMarket('oddeven')
    if (gtype === 'match' || gtype === 'match1' || dtype === 2 || dtype === 4) {
      // Market2: one Back + one Lay column (dtype 2; without dtype: a single odds level).
      const singleLevel = dtype ? dtype === 2 : (m.section || []).every((s) => (s.odds || []).length <= 2)
      if (singleLevel) {
        return (
          <Market2 key={key} market={m} index={i} onBetLockClick={handleBetLockClick} exposureData={exposure} eventId={activeEventId} />
        )
      }
      return (
        <MatchOdds
          key={key}
          {...marketProps(m, i)}
          marketName={mname}
          marketData={getMarketData(mname)}
          exposureData={exposure}
        />
      )
    }
    return null
  }

  // Soccer shows the market picked in MarketList (+ oddeven); other non-cricket sports only oddeven.
  const soccerMarket = isSoccer && selectedMarket && hasMarket(selectedMarket)
    ? marketsData.find((m) => String(m.mname || m.marketName || m.name || '').toLowerCase() === String(selectedMarket).toLowerCase())
    : null
  const oddEvenMarket = marketsData.find((m) => String(m.mname || m.marketName || m.name || '').toLowerCase() === 'oddeven')

  // Reference `v-sumbook`: >0 green, <=0 red; value via toCurrency (en-IN, up to 2 decimals).
  const sumbookColor = (v) => (v > 0 ? 'green' : 'red')
  const toCurrency = (v) => (v == null ? '' : parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }))
  // Reference `v-currency` (bet amount): en-IN, 2 decimals.
  const toAmount = (v) => (v == null || v === '' ? '' : parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

  // Reference My Bets table (Matched / Unmatched tab panes share the markup).
  const renderBetsTable = (bets) => {
    const groups = groupBetsForTable(bets)
    return (
      <div className="table-responsive">
        <table className="table coupon-table mb-0">
          <thead>
            <tr>
              <th style={{ minWidth: '90px' }}>UserName</th>
              <th style={{ minWidth: '90px' }}>Nation</th>
              <th className="text-right" style={{ minWidth: '50px' }}>{'\n                          Rate\n                        '}</th>
              <th className="text-right" style={{ minWidth: '70px' }}>{'\n                          Amount\n                        '}</th>
            </tr>
          </thead>
          {groups.length ? (
            <tbody>
              {groups.map((group, gIdx) => group.bets.map((bet, bIdx) => (
                <React.Fragment key={bet.id || `${gIdx}-${bIdx}`}>
                  <tr className={group.borderClass}>
                    <td colSpan={4}>
                      <b>{group.marketType}</b>{' '}
                      <span className="float-right">{formatBetDate(bet.created_at || group.date)}</span>
                    </td>
                  </tr>
                  <tr className={group.borderClass}>
                    <td className="bt0">{bet.username || bet.user?.username || '-'}</td>
                    <td className="bt0">{bet.selection || bet.selection_name || '-'}</td>
                    <td className="text-right bt0">{formatOdds(bet.odds)}</td>
                    <td className="text-right bt0">{toAmount(bet.amount ?? bet.stake_amount)}</td>
                  </tr>
                  <tr>
                    <td colSpan={4} style={{ height: '3px', padding: 0 }} />
                  </tr>
                </React.Fragment>
              )))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan={4} className="text-center">{'\n                          No records found\n                        '}</td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    )
  }

  return (
    <Layout title="Game Details - Diamond Admin">
      <div className="detail-page-container">
        {/* Market Name List - Soccer Leagues Markets (Only shown for Soccer) */}
        {isSoccer && markets.length > 0 && (
          <MarketList
            markets={markets}
            defaultActive={selectedMarket || markets.find(name => name === 'MATCH_ODDS') || markets[0] || "MATCH_ODDS"}
            onMarketSelect={handleMarketSelect}
          />
        )}{' '}
        <div className="center-main-container">
          <div className="center-content">
            {/* Header section - displays match information */}
            <div className="game-header">
              <span className="game-header-name">
                {matchInfo.series && matchInfo.name
                  ? `${matchInfo.series} > ${matchInfo.name}`
                  : matchInfo.name || 'Loading match details...'}
              </span>{' '}
              <span>
                <span>{matchInfo.time ? formatRefMatchTime(matchInfo.time) : 'Loading...'}</span>{' '}
              </span>
            </div>{' '}

            {/* Non-cricket live score (no reference to compare). Cricket's score card lives in the
                right sidebar's "Score Card" card, like the reference — nothing here. */}
            {activeEventId && matchInfo.iplay && !isCricket && (
              <div className="scorecard-frame">
                <iframe
                  src={scoreCardUrl || `https://scorecard.avrkhub.in/?etid=${activeSportId}&gmid=${activeEventId}`}
                  title="Live Score Card"
                  frameBorder="0"
                  scrolling="no"
                  style={{ width: "100%", height: "100%", overflow: "hidden" }}
                />
              </div>
            )}

            {/* Every market in ONE flex-wrap container, like the reference (`.market-container`
                has margin 0 -15px; each market root carries its own 15px side padding). */}
            <div className="market-container mt-0">
              {/* Cricket, tennis and other sports: every feed market (same dtype dispatch).
                  Soccer keeps the MarketList picker: the selected market + oddeven. */}
              {!isSoccer && orderedMarkets.map(renderMarket)}
              {soccerMarket && renderMarket(soccerMarket, 0)}
              {isSoccer && oddEvenMarket && renderMarket(oddEvenMarket, 1)}
            </div>
          </div>{' '}

          {/* Right Sidebar - Book Summary, Live TV, Score Card, My Bets (reference markup) */}
          <div className="right-sidebar">
            <div className="card mb-2">
              {isCricket && (
                <div className="booksum-box-container mb-2">
                  <div
                    className="booksum-title"
                    data-toggle="collapse"
                    data-target="#demo"
                    onClick={() => setIsBookSummaryOpen(prev => !prev)}
                  >
                    {'\n              Book Summary\n            '}
                  </div>{' '}
                  <div id="demo" className={`booksum-desc collapse${isBookSummaryOpen ? ' show' : ''}`}>
                    {(matchExposure?.list?.length > 0) ? (
                      matchExposure.list.map((row, idx) => (
                        <div key={`${row.marketId}-${row.name}-${idx}`} className="booksum-box">
                          <span>{row.name}</span>{' '}
                          <span style={{ color: sumbookColor(row.exposure) }}>{toCurrency(row.exposure)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="booksum-box"><span>No Data Found</span></div>
                    )}
                  </div>
                </div>
              )}{' '}
              {matchInfo.tv && (
                <>
                  <div
                    className="card-header pointer"
                    data-toggle="collapse"
                    data-target=".video-tv"
                    aria-expanded={isTvExpanded ? 'true' : 'false'}
                    onClick={() => setIsTvExpanded(!isTvExpanded)}
                  >
                    <h6 className="card-title">
                      <a href="#" title="" onClick={(e) => e.preventDefault()}>
                        <img className="mr-1" src="/static/admin/img/arrow-down.svg" alt="" />
                      </a>
                      {'\n              Live Match\n            '}
                    </h6>
                  </div>{' '}
                  <div className={`video-tv collapse${isTvExpanded ? ' show' : ''}`}>
                    {isTvExpanded && (
                      <iframe
                        key={useFallbackStream ? 'fallback' : 'primary'}
                        src={useFallbackStream
                          ? `https://live-stream.softgamingapi.com/directStream?gmid=${activeEventId}`
                          : `https://stream-s-43.uhdmovies.online/sports-stream?id=${activeEventId}`}
                        allow="autoplay"
                        onLoad={() => { streamLoadedRef.current = true }}
                        onError={() => setUseFallbackStream(true)}
                      />
                    )}
                  </div>
                </>
              )}
            </div>{' '}
            <div className="mb-2">
              <div className="card-header" style={isCricket ? undefined : { display: 'none' }}>
                <h6 className="card-title float-left">Score Card</h6>
              </div>{' '}
              {/* Reference fills this card from its live score socket; our backend only gives the
                  score iframe, shown here while the match is in play. */}
              <div>
                {isCricket && activeEventId && matchInfo.iplay && (
                  <div className="scorecard-frame scorecard-frame-cricket">
                    <iframe
                      src={`https://scorecard.avrkhub.in/?etid=${activeSportId}&gmid=${activeEventId}&v=5`}
                      title="Live Score Card"
                      frameBorder="0"
                      scrolling="no"
                      style={{ width: '100%', height: '100%', overflow: 'hidden' }}
                    />
                  </div>
                )}
              </div>
            </div>{' '}
            {/* My Bets Section - Tabbed interface for matched/unmatched bets */}
            <div id="my-game-bets" className="card mb-2 my-bet">
              <div className="card-header">
                <h6 className="card-title float-left">My Bets</h6>{' '}
                <a
                  href="#"
                  className="btn btn-back float-right"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowViewMoreModal(true)
                  }}
                >
                  View More
                </a>
              </div>{' '}
              <div className="card-body">
                <div className="tabs">
                  <div className="">
                    <ul role="tablist" className="nav nav-tabs small">
                      <li role="presentation" className="nav-item">
                        <a
                          role="tab"
                          aria-selected={activeTab === 'matched' ? 'true' : 'false'}
                          href="#"
                          target="_self"
                          className={`nav-link${activeTab === 'matched' ? ' active' : ''}`}
                          onClick={(e) => {
                            e.preventDefault()
                            setActiveTab('matched')
                          }}
                        >
                          Matched Bets
                        </a>
                      </li>
                      <li role="presentation" className="nav-item">
                        <a
                          role="tab"
                          aria-selected={activeTab === 'unmatched' ? 'true' : 'false'}
                          href="#"
                          target="_self"
                          className={`nav-link${activeTab === 'unmatched' ? ' active' : ''}`}
                          onClick={(e) => {
                            e.preventDefault()
                            setActiveTab('unmatched')
                          }}
                        >
                          Unmatched Bets
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="tab-content">
                    <div
                      role="tabpanel"
                      aria-hidden={activeTab === 'matched' ? 'false' : 'true'}
                      className={`tab-pane${activeTab === 'matched' ? ' active' : ''}`}
                      style={activeTab === 'matched' ? undefined : { display: 'none' }}
                    >
                      <div id="matched-bet" className="tab-pane active">
                        {renderBetsTable(matchedBets)}
                      </div>
                    </div>{' '}
                    <div
                      role="tabpanel"
                      aria-hidden={activeTab === 'unmatched' ? 'false' : 'true'}
                      className={`tab-pane${activeTab === 'unmatched' ? ' active' : ''}`}
                      style={activeTab === 'unmatched' ? undefined : { display: 'none' }}
                    >
                      <div id="unmatched-bet" className="tab-pane">
                        {renderBetsTable(unmatchedBets)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>{' '}
        {/* Reference "mobile" (< 1280px): floating bet-count shortcut to My Bets */}
        {IS_REF_MOBILE && (
          <div
            className="market-show-icon d-none-desktop"
            onClick={() => document.getElementById('my-game-bets')?.scrollIntoView()}
          >
            <div className="bet-cnt">{`\n      ${matchedBets.length}\n    `}</div>{' '}
            <div className="bet-title">Bets</div>
          </div>
        )}
      </div>

      {/* Bet Lock Modal - Account selection and transaction code input */}
      <BetLockModal
        show={showBetLockModal}
        onHide={() => setShowBetLockModal(false)}
        sportId={activeSportId}
        eventId={activeEventId}
        marketId={betLockMarketId || activeMarketId}
        marketName={betLockMarketName}
      />
      <SportsViewMoreBetsModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        matchId={activeEventId}
      />
    </Layout>
  )
}

export default GameDetails


